import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Search, User, Building2, Mail, Calendar, ArrowLeft } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { API_BASE } from '../../config/api';
import UserAvatar from '../../components/UserAvatar';

const AdminUsers = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load user details if userId is provided
  useEffect(() => {
    if (userId) {
      loadUserDetails(userId);
    }
  }, [userId]);

  const searchUsers = async () => {
    if (!searchQuery || searchQuery.length < 2) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(
        `${API_BASE}/admin/support/users/search?q=${encodeURIComponent(searchQuery)}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );
      
      if (!response.ok) {
        throw new Error('Search failed');
      }
      
      const data = await response.json();
      setSearchResults(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadUserDetails = async (id) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(
        `${API_BASE}/admin/support/users/${id}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to load user details');
      }
      
      const data = await response.json();
      setSelectedUser(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUserSelect = (user) => {
    navigate(`/admin/support/users/${user.id}`);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      searchUsers();
    }
  };

  if (selectedUser) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSelectedUser(null);
              navigate('/admin/support/users');
            }}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Search
          </Button>
        </div>

        {/* User Profile Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-4">
              <UserAvatar
                src={selectedUser.avatar_url}
                name={selectedUser.name}
                size="h-16 w-16"
                fallback={<User className="h-8 w-8 text-muted-foreground" />}
              />
              <div>
                <CardTitle className="flex items-center gap-2">
                  {selectedUser.name}
                  {selectedUser.is_admin && (
                    <Badge variant="default">Admin</Badge>
                  )}
                </CardTitle>
                <p className="text-muted-foreground">{selectedUser.email}</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">User ID:</span>
                <p className="font-mono text-xs">{selectedUser.id}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Account Type:</span>
                <p className="capitalize">{selectedUser.account_type}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Created:</span>
                <p>{new Date(selectedUser.created_at).toLocaleDateString()}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Last Active:</span>
                <p>{selectedUser.last_active ? new Date(selectedUser.last_active).toLocaleDateString() : 'N/A'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Workspace Memberships */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Workspace Memberships ({selectedUser.workspaces?.length || 0})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {selectedUser.workspaces?.length > 0 ? (
              <div className="space-y-3">
                {selectedUser.workspaces.map((ws) => (
                  <div
                    key={ws.workspace_id}
                    className="flex items-center justify-between p-3 bg-muted rounded-lg cursor-pointer hover:bg-muted/80"
                    onClick={() => navigate(`/admin/support/workspaces/${ws.workspace_id}`)}
                  >
                    <div>
                      <p className="font-medium">{ws.workspace_name}</p>
                      <p className="text-sm text-muted-foreground">
                        {ws.plan_name} • Seats: {ws.seat_usage}
                      </p>
                    </div>
                    <Badge variant={ws.role === 'owner' ? 'default' : 'secondary'}>
                      {ws.role}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground">No workspace memberships</p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground">Users</h2>
        <p className="text-muted-foreground">Search and view user details</p>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by email, name, or user ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={handleKeyPress}
                className="pl-10"
              />
            </div>
            <Button onClick={searchUsers} disabled={loading || searchQuery.length < 2}>
              {loading ? 'Searching...' : 'Search'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {error && (
        <div className="bg-destructive/10 text-destructive p-4 rounded-lg">
          {error}
        </div>
      )}

      {/* Search Results */}
      {searchResults.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Search Results ({searchResults.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {searchResults.map((user) => (
                <div
                  key={user.id}
                  className="flex items-center justify-between p-3 bg-muted rounded-lg cursor-pointer hover:bg-muted/80 transition-colors"
                  onClick={() => handleUserSelect(user)}
                >
                  <div className="flex items-center gap-3">
                    <UserAvatar
                      src={user.avatar_url}
                      name={user.name}
                      size="h-10 w-10"
                      fallback={<User className="h-5 w-5 text-muted-foreground" />}
                    />
                    <div>
                      <p className="font-medium flex items-center gap-2">
                        {user.name}
                        {user.is_admin && (
                          <Badge variant="default" className="text-xs">Admin</Badge>
                        )}
                      </p>
                      <p className="text-sm text-muted-foreground">{user.email}</p>
                    </div>
                  </div>
                  <div className="text-right text-sm text-muted-foreground">
                    <p>{user.workspace_count} workspace(s)</p>
                    <p>{new Date(user.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminUsers;
